import { useEffect, useState } from "react";
import { cardAssetIdFromUrl } from "@/lib/imaging/pipeline";
import { cn } from "@/lib/utils";

/**
 * Listing image that follows the asynchronous imaging pipeline.
 *
 * The stable asset URL serves the seller's uploaded photo while the studio
 * render is being produced. This component polls the asset status and swaps
 * itself to the processed version the moment it is ready, without a reload.
 * For any non-asset URL it behaves exactly like a plain <img>.
 */

type Props = React.ImgHTMLAttributes<HTMLImageElement> & { src: string; alt: string };

const POLL_MS = 5000;
const MAX_POLLS = 60; // ~5 minutes, then stop and leave the uploaded photo.

/** True when this URL is served by the card asset endpoint. */
export function isCardAssetUrl(url: string | null | undefined): boolean {
  return Boolean(url && cardAssetIdFromUrl(url));
}

export function CardAssetImage({ src, className, alt, ...rest }: Props) {
  const assetId = cardAssetIdFromUrl(src);
  const [version, setVersion] = useState(0);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!assetId) return;
    setVersion(0);
    setProcessing(false);

    let cancelled = false;
    let timer: number | undefined;
    let tries = 0;
    // Probe the status endpoint on this app's own origin: stored URLs may be
    // absolute to the canonical domain, which is cross-origin in previews.
    const rawBase = src.split("?")[0]!;
    const base = rawBase.replace(/^https?:\/\/[^/]+(?=\/api\/public\/card-image\/)/, "");

    const schedule = () => {
      if (cancelled || ++tries >= MAX_POLLS) return;
      timer = window.setTimeout(tick, POLL_MS);
    };

    const tick = async () => {
      if (cancelled) return;
      // Don't burn requests or battery on a backgrounded tab / offline device.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        schedule();
        return;
      }
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        schedule();
        return;
      }
      try {
        const res = await fetch(`${base}?v=status`, { cache: "no-store" });
        if (cancelled) return;
        if (!res.ok) {
          // 404 means the asset row is gone — stop polling entirely.
          if (res.status === 404) {
            setProcessing(false);
            return;
          }
          schedule();
          return;
        }
        const { status } = (await res.json()) as { status?: string };
        if (cancelled) return;
        if (status === "ready") {
          setProcessing(false);
          setVersion((v) => v + 1); // re-request the stable URL, now processed
          return;
        }
        if (status === "failed") {
          setProcessing(false);
          return;
        }
        setProcessing(true);
        schedule();
      } catch {
        // Transient network issue — the uploaded photo stays on screen and we
        // try again on the next tick instead of giving up silently.
        if (!cancelled) schedule();
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [assetId, src]);

  const finalSrc = version > 0 ? `${src}${src.includes("?") ? "&" : "?"}r=${version}` : src;

  return (
    <img
      decoding="async"
      {...rest}
      key={finalSrc}
      alt={alt}
      src={finalSrc}
      aria-busy={processing || undefined}
      className={cn(className, "animate-fade-in", processing && "opacity-95")}
    />
  );
}
